import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import VisibilityToggle from "../../ui/VisibilityToggle";
import LocationPrecisionToggle from "../../ui/LocationPrecisionToggle";
import ToggleSwitch from "../settings/fields/ToggleSwitch";
import Spinner from "../../ui/Spinner";
import { useSettings } from "../../contexts/SettingsContext";
import ImageCropModal from "./ImageCropModal";
import { MAX_NOTE_IMAGES } from "../../utils/constants";

export default function EditNoteModal({ onClose, onSave, note }) {
  const { settings } = useSettings();
  const [title, setTitle] = useState(note.title || "");
  const [noteBody, setNoteBody] = useState(note.body || "");
  const [visibility, setVisibility] = useState(note.visibility || "public");
  const [locationPrecision, setLocationPrecision] = useState(
    note.locationPrecision || "exact",
  );
  const [showAddress, setShowAddress] = useState(note.showAddress !== false);
  const [existingImages, setExistingImages] = useState(note.images || []);
  const [newImages, setNewImages] = useState([]); // { blob: Blob, previewUrl: string }[]
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [cropFiles, setCropFiles] = useState(null); // File[] | null
  const fileInputRef = useRef(null);

  const maxImages = MAX_NOTE_IMAGES;

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const remaining = maxImages - existingImages.length - newImages.length;
      if (remaining <= 0) return;
      setCropFiles(files.slice(0, remaining));
    }
    e.target.value = null;
  };

  const handleCropComplete = (blobs) => {
    const withPreviews = blobs.map((blob) => ({
      blob,
      previewUrl: URL.createObjectURL(blob),
    }));
    setNewImages((prev) => [...prev, ...withPreviews]);
    setCropFiles(null);
  };

  const handleCropCancel = () => {
    setCropFiles(null);
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewImage = (index) => {
    setNewImages((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSave = async () => {
    let formErrors = {};
    if (!title.trim()) formErrors.title = "Please fill out this field.";
    if (!noteBody.trim()) formErrors.noteBody = "Please fill out this field.";

    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      setSubmitting(true);
      try {
        await onSave({
          title,
          noteBody,
          visibility,
          locationPrecision,
          showAddress,
          existingImages,
          newImages: newImages.map((img) => img.blob),
        });
        onClose();
      } finally {
        setSubmitting(false);
      }
    }
  };

  const isCropping = cropFiles !== null;

  const handleClose = () => {
    if (isCropping) return;
    newImages.forEach((img) => {
      if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
    });
    setTitle("");
    setNoteBody("");
    setExistingImages([]);
    setNewImages([]);
    setErrors({});
    onClose();
  };

  const totalImages = existingImages.length + newImages.length;

  const modal = (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[60] p-4">
      <div className="bg-surface-elevated rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col relative border border-border-light">
        {/* Header - Fixed */}
        <div className="px-6 pt-6 pb-4 border-b border-border-strong">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-text-muted hover:text-text-secondary transition"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>

          <h2 className="text-2xl font-bold text-text-primary">Edit Note</h2>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 pretty-scroll">
          <div className="mb-5">
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Title
            </label>
            <input
              type="text"
              className={`w-full bg-surface-elevated border rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:ring-2 transition disabled:opacity-50 ${
                errors.title
                  ? "border-destructive focus:ring-red-300"
                  : "border-strong focus:ring-primary-300"
              }`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
            />
            {errors.title && (
              <p className="text-destructive text-xs mt-1">{errors.title}</p>
            )}
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Note
            </label>
            <textarea
              className={`w-full bg-surface-elevated border rounded-lg px-4 py-2 text-text-primary focus:outline-none focus:ring-2 transition resize-none disabled:opacity-50 ${
                errors.noteBody
                  ? "border-destructive focus:ring-red-300"
                  : "border-strong focus:ring-primary-300"
              }`}
              rows="5"
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              disabled={submitting}
            ></textarea>
            {errors.noteBody && (
              <p className="text-destructive text-xs mt-1">{errors.noteBody}</p>
            )}
          </div>

          <div className="mb-5 flex items-center gap-3">
            <span className="text-sm font-medium text-text-secondary">
              Visibility:
            </span>
            <VisibilityToggle
              value={visibility}
              onChange={setVisibility}
              size="md"
              disabled={submitting}
            />
          </div>

          <div className="mb-5 flex items-center gap-3">
            <span className="text-sm font-medium text-text-secondary">
              Precision:
            </span>
            <LocationPrecisionToggle
              value={locationPrecision}
              onChange={setLocationPrecision}
              size="md"
              disabled={submitting}
              units={settings?.units || "miles"}
            />
          </div>

          <div className="mb-5 flex items-center justify-between">
            <span className="text-sm font-medium text-text-secondary">
              Show address on note
            </span>
            <ToggleSwitch
              id="edit-show-address-toggle"
              checked={showAddress}
              onChange={setShowAddress}
              ariaLabel="Toggle address visibility on note"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              Upload Images
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                disabled={submitting || isCropping}
                onClick={() => {
                  if (totalImages > 0) {
                    setCropFiles([]);
                  } else {
                    fileInputRef.current.click();
                  }
                }}
                className="file:mr-4 file:py-2 file:px-4 py-2 px-4 rounded-2xl border border-border-strong font-semibold bg-primary text-text-inverse hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {totalImages > 0 ? "Edit images" : "Add images"}
              </button>
              {totalImages > 0 && (
                <span className="text-sm text-text-secondary">
                  {totalImages} image{totalImages > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              onChange={handleImageUpload}
              className="hidden"
            />
            {totalImages > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {existingImages.map((image, index) => (
                  <div
                    key={`existing-${index}`}
                    className="w-10 h-[53px] rounded-md overflow-hidden ring-1 ring-border"
                  >
                    <img
                      src={typeof image === "string" ? image : image.src}
                      alt=""
                      className="w-full h-full object-cover pointer-events-none"
                    />
                  </div>
                ))}
                {newImages.map((img, index) => (
                  <div
                    key={`new-${index}`}
                    className="w-10 h-[53px] rounded-md overflow-hidden ring-1 ring-border"
                  >
                    <img
                      src={img.previewUrl}
                      alt=""
                      className="w-full h-full object-cover pointer-events-none"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="px-6 py-4 border-t border-border-strong flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={submitting || isCropping}
            className="px-5 py-2 bg-surface-sunken text-text-secondary rounded-lg hover:bg-surface-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || isCropping}
            className="px-5 py-2 bg-primary text-text-inverse rounded-lg hover:bg-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <Spinner size={16} />}
            {submitting ? "Updating..." : "Update Note"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modal, document.body)}
      {cropFiles && (
        <ImageCropModal
          initialFiles={cropFiles}
          onSave={handleCropComplete}
          onClose={handleCropCancel}
          existingImageCount={existingImages.length + newImages.length}
          existingImagePreviews={[
            ...existingImages.map((img) =>
              typeof img === "string" ? img : img.src,
            ),
            ...newImages.map((img) => img.previewUrl),
          ]}
          onRemoveExisting={(index) => {
            if (index < existingImages.length) {
              handleRemoveExistingImage(index);
            } else {
              handleRemoveNewImage(index - existingImages.length);
            }
          }}
        />
      )}
    </>
  );
}
